import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Format-keyed DataSource Writer for fixed-width files. Loads a staged FWF file
 * into the DataSource Link's ingest table, applying the Link's parse config
 * (column offsets/lengths) in a single pass. The format/mapping comes from the
 * Link's `parse_config` (which must be `fwf`), not from the socket — pair this
 * with the connection-keyed adapter that staged the file.
 */
export const fwfDataSourceWriterStep = defineStepConfig({
  type: "fwf_datasource_writer",
  name: "Fwf DataSource Writer",
  description:
    "Load a staged fixed-width file into a DataSource Link's data sink, applying the Link's FWF parse config (column offsets/lengths).",
  category: "DataSource Writer",

  experimental: {
    reason:
      "Fwf DataSource Writer is under active development and not yet validated for production workflows.",
  },

  inputSchema: z.object({
    blobStorageUri: z
      .string()
      .describe("URI of the source fixed-width file to load"),
    dataSourceLink: z
      .string()
      .uuid()
      .describe(
        "DataSource Link identifying the source connection + credential, the FWF parse config, and the target schema + table",
      ),
    resumeRowOffset: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(
        "Manually resume from this row instead of row 0 — paste the `resumeRowOffset` from a failed run's last heartbeat details when starting a fresh rerun after all retries were exhausted. Ignored when this attempt already has its own heartbeat checkpoint (same-execution automatic retry).",
      ),
  }),

  outputSchema: z.object({
    rowCount: z.number().describe("Rows successfully upserted"),
    failedCount: z
      .number()
      .describe("Rows that failed and were dead-lettered to .failed.csv"),
    newlyAbsent: z
      .number()
      .optional()
      .describe(
        "trackAbsence only: table rows newly stamped absent_since because this load no longer carries their key",
      ),
    reasserted: z
      .number()
      .optional()
      .describe(
        "trackAbsence only: previously-absent rows whose key reappeared in this load (absent_since cleared)",
      ),
    deletedAbsent: z
      .number()
      .optional()
      .describe(
        "deleteAbsentAfterDays only: table rows deleted because they stayed absent past the grace period. Each delete streams to CDC subscribers of this table as a change with operation 'delete'.",
      ),
    deleteGuardTripped: z
      .boolean()
      .optional()
      .describe(
        "deleteAbsentAfterDays only: true when the delete pass was refused because the absent share of the table exceeded maxDeleteFraction. Nothing was deleted; investigate the feed.",
      ),
  }),

  configSchema: z.object({
    batchSize: z
      .number()
      .int()
      .positive()
      .prefault(500)
      .describe("Number of rows per SQL insert batch"),
    skipUnchangedRows: z
      .boolean()
      .prefault(false)
      .describe(
        "Leave a conflicting row untouched when every value already matches, instead of rewriting it. Cuts WAL and downstream CDC traffic on re-ingests of unchanged data. Off by default: it stops refreshing updated_at on unchanged rows, so that column comes to mean 'when this row last changed' rather than 'when the source last asserted it' — do not enable it for a table whose readers rely on the latter. Automatically ignored for a table with a column type the guard cannot compare exactly.",
      ),
    exclusiveLoad: z
      .boolean()
      .prefault(false)
      .describe(
        "Backfill mode. Loads into an EMPTY destination table with its secondary indexes dropped, then rebuilds them, in one transaction. Measured on a prototype at 9.2x faster publish and 2.8x faster overall. Only for an initial load or a re-baseline into a fresh table: it verifies the destination is empty under an exclusive lock and silently falls back to the ordinary path if it is not, it blocks other writers for the whole load, and it has NO mid-load resume — a failure rolls the load back and a retry starts from row zero. Unique and primary-key constraints are kept, so a duplicate key inside the load is dead-lettered rather than collapsed.",
      ),
    trackAbsence: z
      .boolean()
      .prefault(false)
      .describe(
        "FULL-STATE exports only. After a fully committed load, stamp absent_since on table rows whose key the file no longer carries, and clear it on rows the file re-asserts. A full-state file says what IS; a vanished row is an absence, not an event, and unlike updated_at this signal survives the skip-unchanged guard (it is computed from the load's key set, not from which rows get rewritten). NEVER enable on incremental feeds: everything the current file doesn't repeat would be stamped absent.",
      ),
    deleteAbsentAfterDays: z
      .number()
      .int()
      .min(1)
      .max(365)
      .optional()
      .describe(
        "Requires trackAbsence; incompatible with exclusiveLoad. After the absence pass, DELETE table rows whose absent_since is older than this many days, so the removal reaches CDC subscribers of this table as an ordinary change with operation 'delete' (before-image keyed by the table's unique business key; the writer sets REPLICA IDENTITY to that key's index the first time it deletes). The grace period is the safety net: a truncated or partial file only stamps absence, and the stamp is cleared when the key reappears, so nothing is deleted unless the row stays gone this many consecutive days.",
      ),
    maxDeleteFraction: z
      .number()
      .min(0.01)
      .max(1)
      .prefault(0.2)
      .describe(
        "Delete-pass circuit breaker. When the rows eligible for deletion exceed this share of the table, nothing is deleted and the run reports deleteGuardTripped - a mass disappearance that PERSISTED past the grace period looks more like a broken feed than a real removal of that much of the book.",
      ),
  }),

  emits: [] as const,

  // Hard-coded fanout zip (size 1 → one file per run): the connection-keyed
  // adapter emits index-aligned blobStorageUris + dataSourceLinks arrays into
  // this writer's two single-value sockets. `batch` makes the engine lockstep-zip
  // them (one URI + its own link per run) instead of cross-producing. Fixed on
  // the step type, so it's not an editable config field and never shows in the UI.
  batch: { size: 1 },

  execution: {
    // Generous ceiling per attempt: a legitimate load can run long (multi-million
    // row files, ingest-pool contention from parallel writers, dirty files that
    // degrade to bisected retries). Liveness is enforced by the 2-minute
    // heartbeatTimeout + resume checkpoint, not this cap.
    startToCloseTimeout: "4 hours",
    // Deploys restart workers frequently, and each restart looks like a dead
    // activity to Temporal (heartbeat timeout) — a routine event, not a
    // processing failure. A high ceiling lets a long file load survive many
    // redeploys across its run; each retry resumes from the last committed
    // checkpoint instead of restarting at row 0, so extra attempts are cheap.
    // `maximumInterval` caps backoff growth so a retry after a worker restart
    // reschedules quickly rather than waiting an ever-longer interval.
    retry: {
      maximumAttempts: 8,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
      maximumInterval: "1 minute",
    },
  },
});
